import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useDataCache } from '../contexts/BreakdownDataCacheContext';
import { getScheduleById, getSchedulesByBreakdownId, getSlotsByScheduleId, getCalDatesByScheduleId } from '../api/schedule';
import { getScenesByBreakdownId, getBreakdownById } from '../api/breakdown';
import { getElementRelationsByBreakdownID, getElementsByBreakdownId } from '../api/element';
import { ScheduleInfo, SlotInfo, CalDateInfo } from '../types/schedule.types';
import { SceneInfo } from '../types/scene.types';
import { ElementSceneInfo } from '../types/element.types';

/**
 * 日程页（/schedule）数据加载 Hook 配置
 */
export interface UseScheduleDataLoaderOptions {
  scheduleId: string;
  forceRefresh?: boolean;
}

/**
 * 日程页数据加载：Slot / CalDate / Schedule 信息与关联分场场次缓存。
 */
export function useScheduleDataLoader(options: UseScheduleDataLoaderOptions) {
  const { scheduleId, forceRefresh = false } = options;

  const {
    getSlotCache,
    setSlotCache,
    getCalDateCache,
    setCalDateCache,
    getScheduleCache,
    setScheduleCache,
    getBreakdownCache,
    setBreakdownCache,
    getElementRelationCache,
    setElementRelationCache,
    getElementCache,
    setElementCache,
  } = useDataCache();

  const [schedule, setSchedule] = useState<ScheduleInfo | null>(null);
  const [slots, setSlots] = useState<SlotInfo[]>([]);
  const [calDates, setCalDates] = useState<CalDateInfo[]>([]);
  const [scenes, setScenes] = useState<SceneInfo[]>([]);
  const [elementRelations, setElementRelations] = useState<ElementSceneInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadFromCache = useCallback(() => {
    const slotCache = getSlotCache(scheduleId);
    const calDateCache = getCalDateCache(scheduleId);

    if (slotCache) {
      setSchedule(slotCache.scheduleInfo);
      setSlots(slotCache.slots);
    }

    if (calDateCache) {
      setCalDates(calDateCache.calDates);
    }

    if (slotCache?.scheduleInfo?.scheduleBelongBreakdownID) {
      const breakdownId = slotCache.scheduleInfo.scheduleBelongBreakdownID;
      const breakdownCache = getBreakdownCache(breakdownId);
      const elementRelationCache = getElementRelationCache(breakdownId);
      const elementCache = getElementCache(breakdownId);

      if (breakdownCache) {
        setScenes(breakdownCache.scenes);
      }

      if (elementRelationCache) {
        setElementRelations(elementRelationCache.elementRelations);
      }

      return {
        hasScheduleCache: !!slotCache,
        hasCalDateCache: !!calDateCache,
        hasBreakdownCache: !!breakdownCache,
        hasBreakdownInfo: !!breakdownCache?.breakdownInfo,
        hasElementRelationCache: !!elementRelationCache,
        hasElementCache: !!elementCache,
      };
    }

    return {
      hasScheduleCache: !!slotCache,
      hasCalDateCache: !!calDateCache,
      hasBreakdownCache: false,
      hasBreakdownInfo: false,
      hasElementRelationCache: false,
      hasElementCache: false,
    };
  }, [scheduleId, getSlotCache, getCalDateCache, getBreakdownCache, getElementRelationCache, getElementCache]);

  const loadFromAPI = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const schedulesResponse = await getScheduleById(scheduleId);
      const loadedSchedule = schedulesResponse.data;
      setSchedule(loadedSchedule);

      const [slotsResponse, calDatesResponse] = await Promise.all([
        getSlotsByScheduleId(scheduleId),
        getCalDatesByScheduleId(scheduleId),
      ]);

      const loadedSlots = slotsResponse.data || [];
      const loadedCalDates = calDatesResponse.data || [];

      setSlots(loadedSlots);
      setCalDates(loadedCalDates);

      setSlotCache(scheduleId, {
        scheduleInfo: loadedSchedule,
        slots: loadedSlots,
      });

      setCalDateCache(scheduleId, {
        calDates: loadedCalDates,
      });

      if (loadedSchedule.scheduleBelongBreakdownID) {
        const breakdownId = loadedSchedule.scheduleBelongBreakdownID;
        const scheduleCacheSlice = getScheduleCache(breakdownId);

        let mergedSchedules: ScheduleInfo[];
        if (scheduleCacheSlice?.schedules?.length) {
          mergedSchedules = scheduleCacheSlice.schedules.map((p) =>
            p.scheduleID === scheduleId ? { ...p, ...loadedSchedule } : p
          );
        } else {
          try {
            const listRes = await getSchedulesByBreakdownId(breakdownId);
            const fromApi = listRes.data || [];
            mergedSchedules =
              fromApi.length > 0
                ? fromApi.map((p) =>
                    p.scheduleID === scheduleId ? { ...p, ...loadedSchedule } : p
                  )
                : [loadedSchedule];
          } catch {
            mergedSchedules = [loadedSchedule];
          }
        }
        if (!mergedSchedules.some((p) => p.scheduleID === scheduleId)) {
          mergedSchedules = [...mergedSchedules, loadedSchedule];
        }
        setScheduleCache(breakdownId, {
          schedules: mergedSchedules,
          currentScheduleId: scheduleId,
          currentScheduleInfo: loadedSchedule,
          currentSlots: loadedSlots,
          currentCalDates: loadedCalDates,
        });

        const breakdownCache = getBreakdownCache(breakdownId);
        const elementRelationCache = getElementRelationCache(breakdownId);
        const elementCache = getElementCache(breakdownId);

        if (breakdownCache && elementRelationCache && elementCache && breakdownCache.breakdownInfo) {
          setScenes(breakdownCache.scenes);
          setElementRelations(elementRelationCache.elementRelations);
        } else {
          const [breakdownInfoRes, scenesResponse, relationsResponse, elementsResponse] =
            await Promise.all([
              breakdownCache?.breakdownInfo
                ? Promise.resolve({ data: breakdownCache.breakdownInfo })
                : getBreakdownById(breakdownId),
              breakdownCache
                ? Promise.resolve({ data: breakdownCache.scenes })
                : getScenesByBreakdownId(breakdownId),
              elementRelationCache
                ? Promise.resolve({ data: elementRelationCache.elementRelations })
                : getElementRelationsByBreakdownID(breakdownId),
              elementCache
                ? Promise.resolve({ data: elementCache.elements })
                : getElementsByBreakdownId(breakdownId),
            ]);

          const loadedBreakdownInfo = breakdownInfoRes.data;
          const loadedScenes = scenesResponse.data || [];
          const loadedRelations = relationsResponse.data || [];
          const loadedElements = elementsResponse.data || [];

          setScenes(loadedScenes);
          setElementRelations(loadedRelations);

          setBreakdownCache(breakdownId, {
            breakdownInfo: loadedBreakdownInfo,
            scenes: loadedScenes,
          });
          setElementRelationCache(breakdownId, {
            elementRelations: loadedRelations,
          });
          setElementCache(breakdownId, {
            elements: loadedElements,
          });
        }
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        setSchedule(null);
        setSlots([]);
        setCalDates([]);
        setScenes([]);
        setElementRelations([]);
        setError(null);
      } else {
        console.error('[useScheduleDataLoader] 数据加载失败:', err);
        setError(err instanceof Error ? err.message : '数据加载失败');
      }
    } finally {
      setLoading(false);
    }
  }, [
    scheduleId,
    setSlotCache,
    setCalDateCache,
    getScheduleCache,
    setScheduleCache,
    getBreakdownCache,
    getElementRelationCache,
    setBreakdownCache,
    setElementRelationCache,
    getElementCache,
    setElementCache,
  ]);

  const refresh = useCallback(async () => {
    await loadFromAPI();
  }, [loadFromAPI]);

  useEffect(() => {
    if (!scheduleId) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      if (forceRefresh) {
        await loadFromAPI();
        return;
      }

      const cacheStatus = loadFromCache();

      if (
        cacheStatus.hasScheduleCache &&
        cacheStatus.hasCalDateCache &&
        cacheStatus.hasBreakdownInfo
      ) {
        setLoading(false);
        return;
      }

      await loadFromAPI();
    };

    loadData();
  }, [scheduleId, forceRefresh, loadFromCache, loadFromAPI]);

  return {
    schedule,
    slots,
    calDates,
    scenes,
    elementRelations,
    loading,
    error,
    refresh,
  };
}
